import type { ApiClient } from "@carepass/api-client";
import type { Schemas } from "../workspace/api.generated";
import type { BenefitSearchFormValues } from "./schema";

export interface BenefitListQuery {
  readonly status: BenefitSearchFormValues["status"];
  readonly memberId?: number;
  readonly from: string;
  readonly to: string;
  readonly page: number;
  readonly size: number;
}

export async function getBenefits(
  apiClient: ApiClient,
  query: BenefitListQuery,
  signal?: AbortSignal,
): Promise<Schemas["AdminPageBenefitClaimView"]> {
  const response = await apiClient.get<Schemas["AdminPageBenefitClaimView"]>(
    "/api/v1/admin/benefits/search",
    {
      query: {
        status: query.status || undefined,
        memberId: query.memberId,
        from: query.from || undefined,
        to: query.to || undefined,
        page: query.page,
        size: query.size,
      },
      signal,
      cache: "no-store",
    },
  );
  return response.data;
}

export async function getBenefitDetail(
  apiClient: ApiClient,
  claimId: number,
  signal?: AbortSignal,
): Promise<Schemas["BenefitAdminDetailView"]> {
  const response = await apiClient.get<Schemas["BenefitAdminDetailView"]>(
    `/api/v1/admin/benefits/${claimId}`,
    { signal, cache: "no-store" },
  );
  return response.data;
}

export async function requestBenefitApproval(
  apiClient: ApiClient,
  claimId: number,
  input: Schemas["RequestBenefitApprovalRequest"],
  signal?: AbortSignal,
): Promise<Schemas["BenefitClaimView"]> {
  const response = await apiClient.post<
    Schemas["BenefitClaimView"],
    Schemas["RequestBenefitApprovalRequest"]
  >(`/api/v1/admin/benefits/${claimId}/approval-requests`, input, {
    signal,
    cache: "no-store",
  });
  return response.data;
}

export async function rejectBenefitClaim(
  apiClient: ApiClient,
  claimId: number,
  input: Schemas["BenefitReasonRequest"],
  signal?: AbortSignal,
): Promise<Schemas["BenefitClaimView"]> {
  const response = await apiClient.post<
    Schemas["BenefitClaimView"],
    Schemas["BenefitReasonRequest"]
  >(`/api/v1/admin/benefits/${claimId}/reject`, input, {
    signal,
    cache: "no-store",
  });
  return response.data;
}

export async function decideBenefitApproval(
  apiClient: ApiClient,
  approvalId: number,
  input: Schemas["DecideBenefitApprovalRequest"],
  signal?: AbortSignal,
): Promise<Schemas["BenefitClaimView"]> {
  const response = await apiClient.post<
    Schemas["BenefitClaimView"],
    Schemas["DecideBenefitApprovalRequest"]
  >(`/api/v1/admin/benefits/approval-requests/${approvalId}/decision`, input, {
    signal,
    cache: "no-store",
  });
  return response.data;
}

export async function recordManualBenefitPayout(
  apiClient: ApiClient,
  claimId: number,
  input: Schemas["RecordManualBenefitPayoutRequest"],
  signal?: AbortSignal,
): Promise<Schemas["BenefitClaimView"]> {
  const response = await apiClient.post<
    Schemas["BenefitClaimView"],
    Schemas["RecordManualBenefitPayoutRequest"]
  >(`/api/v1/admin/benefits/${claimId}/manual-payout`, input, {
    signal,
    cache: "no-store",
  });
  return response.data;
}

